import type { Rect } from '../../doc/geometry';

/**
 * Raster brushes for sketch sheets. A stroke is a chain of round stamps along the smoothed
 * pointer path. Paint brushes draw at `flow` onto a scratch canvas that is composited onto the
 * layer once, at `opacity`, when the stroke ends, so a translucent stroke never builds up where
 * it crosses itself. The eraser works straight on the layer with destination-out, its per-stamp
 * alpha corrected for the overlap so the whole stroke erases `opacity` of what was there.
 */

export type BrushKind = 'pencil' | 'fineliner' | 'brush' | 'eraser' | 'fill' | 'picker';

export const STROKE_BRUSHES: BrushKind[] = ['pencil', 'fineliner', 'brush', 'eraser'];

export interface BrushSettings {
  /** Diameter, sheet pixels. */
  size: number;
  /** 0..1, the whole stroke's opacity. */
  opacity: number;
  /** 0..1: 1 is a hard edge, lower fades from the centre. */
  hardness: number;
  /** 0..1, per-stamp alpha (paint brushes). */
  flow: number;
  pressureSize: boolean;
  pressureOpacity: boolean;
}

export interface BrushParams extends BrushSettings {
  kind: BrushKind;
  /** CSS hex colour. */
  color: string;
}

export const BRUSH_LABEL: Record<BrushKind, string> = {
  pencil: 'Pencil',
  fineliner: 'Fineliner',
  brush: 'Brush',
  eraser: 'Eraser',
  fill: 'Fill',
  picker: 'Eyedropper',
};

export const BRUSH_SIZE_RANGE: Record<BrushKind, [number, number]> = {
  pencil: [1, 64],
  fineliner: [1, 64],
  brush: [2, 400],
  eraser: [2, 400],
  fill: [1, 1],
  picker: [1, 1],
};

/** Which controls a brush offers. */
export const BRUSH_CONTROLS: Record<BrushKind, { hardness: boolean; flow: boolean; pressure: boolean }> = {
  pencil: { hardness: false, flow: false, pressure: true },
  fineliner: { hardness: false, flow: false, pressure: false },
  brush: { hardness: true, flow: true, pressure: true },
  eraser: { hardness: true, flow: false, pressure: true },
  fill: { hardness: false, flow: false, pressure: false },
  picker: { hardness: false, flow: false, pressure: false },
};

/** Tiles the paint undo backs up (see rasterStore). */
export const TILE = 256;

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function parseHex(color: string): RGB {
  const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export const toHex = (c: RGB): string => '#' + [c.r, c.g, c.b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Grows a rect to include a circle. */
function include(r: Rect | null, x: number, y: number, radius: number): Rect {
  const x0 = Math.floor(x - radius - 1);
  const y0 = Math.floor(y - radius - 1);
  const x1 = Math.ceil(x + radius + 1);
  const y1 = Math.ceil(y + radius + 1);
  if (!r) return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  const nx0 = Math.min(r.x, x0);
  const ny0 = Math.min(r.y, y0);
  const nx1 = Math.max(r.x + r.w, x1);
  const ny1 = Math.max(r.y + r.h, y1);
  return { x: nx0, y: ny0, w: nx1 - nx0, h: ny1 - ny0 };
}

/** Clips a rect to a canvas. Null when nothing is left. */
export function clipRect(r: Rect, w: number, h: number): Rect | null {
  const x0 = Math.max(0, r.x);
  const y0 = Math.max(0, r.y);
  const x1 = Math.min(w, r.x + r.w);
  const y1 = Math.min(h, r.y + r.h);
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export interface StrokePoint {
  x: number;
  y: number;
  /** 0..1 */
  pressure: number;
}

/**
 * One stroke in progress. `layer` receives the pixels; `scratch` (same size) holds paint
 * brushes until the end. `backup(rect)` is called before pixels of the layer change, so the
 * caller can copy the tiles it needs for undo.
 */
export class StrokeSession {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly target: CanvasRenderingContext2D;
  private readonly direct: boolean;
  private readonly rgb: RGB;
  private readonly spacing: number;
  private last: StrokePoint | null = null;
  private smooth: StrokePoint | null = null;
  private carry = 0;
  private dirty: Rect | null = null;
  private stamps = 0;

  constructor(
    private readonly layer: HTMLCanvasElement,
    private readonly scratch: HTMLCanvasElement,
    private readonly p: BrushParams,
    private readonly backup: (rect: Rect) => void,
  ) {
    this.direct = p.kind === 'eraser';
    this.rgb = parseHex(p.color);
    const hard = this.p.hardness >= 0.99 || p.kind === 'pencil' || p.kind === 'fineliner';
    this.spacing = Math.max(0.5, p.size * (hard ? 0.2 : 0.1));
    const lctx = layer.getContext('2d');
    const sctx = scratch.getContext('2d');
    if (!lctx || !sctx) throw new Error('2D canvas unavailable');
    this.ctx = lctx;
    this.target = this.direct ? lctx : sctx;
    if (!this.direct) {
      sctx.setTransform(1, 0, 0, 1, 0, 0);
      sctx.globalCompositeOperation = 'source-over';
      sctx.clearRect(0, 0, scratch.width, scratch.height);
    }
  }

  get bounds(): Rect | null {
    return this.dirty;
  }

  private effectiveSize(pressure: number): number {
    const p = this.p;
    if (!p.pressureSize || !BRUSH_CONTROLS[p.kind].pressure) return p.size;
    return Math.max(0.5, p.size * (0.15 + 0.85 * clamp01(pressure)));
  }

  private effectiveAlpha(pressure: number): number {
    const p = this.p;
    const base = this.direct ? 1 : p.flow;
    const withPressure = p.pressureOpacity && BRUSH_CONTROLS[p.kind].pressure ? base * (0.2 + 0.8 * clamp01(pressure)) : base;
    if (!this.direct) return clamp01(withPressure);
    // eraser: stamps overlap about size/spacing times along the path; pick a per-stamp alpha
    // whose accumulation over that many stamps equals the wanted opacity
    const n = Math.max(1, this.effectiveSize(pressure) / this.spacing);
    const total = clamp01(p.opacity * withPressure);
    return total >= 1 ? 1 : 1 - Math.pow(1 - total, 1 / n);
  }

  begin(pt: StrokePoint): void {
    this.last = pt;
    this.smooth = pt;
    this.stamp(pt.x, pt.y, pt.pressure);
  }

  extend(raw: StrokePoint): void {
    if (!this.last || !this.smooth) return this.begin(raw);
    // light position smoothing: fewer wobbles from a shaky hand, still tight to the pointer
    const k = this.p.kind === 'pencil' || this.p.kind === 'fineliner' ? 0.7 : 0.55;
    const pt: StrokePoint = {
      x: this.smooth.x + (raw.x - this.smooth.x) * k,
      y: this.smooth.y + (raw.y - this.smooth.y) * k,
      pressure: raw.pressure,
    };
    this.smooth = pt;
    const from = this.last;
    const dx = pt.x - from.x;
    const dy = pt.y - from.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-3) return;
    let t = this.carry;
    while (t <= len) {
      const f = t / len;
      this.stamp(from.x + dx * f, from.y + dy * f, from.pressure + (pt.pressure - from.pressure) * f);
      t += this.spacing;
    }
    this.carry = t - len;
    this.last = pt;
  }

  private stamp(x: number, y: number, pressure: number): void {
    const size = this.effectiveSize(pressure);
    const r = size / 2;
    const alpha = this.effectiveAlpha(pressure);
    if (this.direct) this.backup(include(null, x, y, r));
    this.dirty = include(this.dirty, x, y, r);
    const ctx = this.target;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.globalCompositeOperation = this.direct ? 'destination-out' : 'source-over';
    const { r: cr, g: cg, b: cb } = this.rgb;
    const hard = this.p.hardness >= 0.99 || !BRUSH_CONTROLS[this.p.kind].hardness;
    if (hard || r < 1) {
      ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
    } else {
      const g = ctx.createRadialGradient(x, y, r * this.p.hardness, x, y, r);
      g.addColorStop(0, `rgba(${cr},${cg},${cb},1)`);
      g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
      ctx.fillStyle = g;
    }
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.5, r), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    this.stamps++;
  }

  /** Composites a paint stroke onto the layer. Returns the changed rect (layer pixels), or null. */
  end(): Rect | null {
    const rect = this.dirty ? clipRect(this.dirty, this.layer.width, this.layer.height) : null;
    if (!rect || !this.stamps) return null;
    if (!this.direct) {
      this.backup(rect);
      const ctx = this.ctx;
      ctx.save();
      ctx.globalAlpha = clamp01(this.p.opacity);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(this.scratch, rect.x, rect.y, rect.w, rect.h, rect.x, rect.y, rect.w, rect.h);
      ctx.restore();
      this.scratch.getContext('2d')?.clearRect(rect.x, rect.y, rect.w, rect.h);
    }
    return rect;
  }

  /** Drops a paint stroke in progress (the caller restores the layer for a direct one). */
  cancel(): void {
    if (this.direct) return;
    const rect = this.dirty ? clipRect(this.dirty, this.scratch.width, this.scratch.height) : null;
    if (rect) this.scratch.getContext('2d')?.clearRect(rect.x, rect.y, rect.w, rect.h);
  }
}

// ---- fill and eyedropper ----------------------------------------------------------------

/**
 * Flood fill from a pixel over the layer: contiguous pixels within `tolerance` (0..255, on the
 * largest channel difference, alpha included) take the colour at `opacity`. The anti-aliased
 * rim the fill stops at (a stroke's soft edge) gets the colour composited underneath it, so no
 * light seam is left along the outline. Returns the changed rect, or null when the seed is out
 * of bounds.
 */
export function floodFill(
  canvas: HTMLCanvasElement,
  sx: number,
  sy: number,
  color: string,
  opacity: number,
  tolerance: number,
  backup: (rect: Rect) => void,
): Rect | null {
  const w = canvas.width;
  const h = canvas.height;
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  if (x0 < 0 || y0 < 0 || x0 >= w || y0 >= h) return null;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const seed = (y0 * w + x0) * 4;
  const sr = d[seed]!;
  const sg = d[seed + 1]!;
  const sb = d[seed + 2]!;
  const sa = d[seed + 3]!;
  const tol = Math.max(0, Math.min(255, tolerance));
  const match = (i: number) => {
    // a fully transparent pixel matches any other fully transparent pixel, whatever its colour
    if (sa === 0 && d[i + 3] === 0) return true;
    return (
      Math.abs(d[i]! - sr) <= tol && Math.abs(d[i + 1]! - sg) <= tol && Math.abs(d[i + 2]! - sb) <= tol && Math.abs(d[i + 3]! - sa) <= tol
    );
  };
  // 0 untouched, 1 filled, 2 rim (a non-matching neighbour of a filled pixel)
  const mark = new Uint8Array(w * h);
  const stack: number[] = [y0 * w + x0];
  const { r, g, b } = parseHex(color);
  const a = clamp01(opacity);
  let minX = x0;
  let minY = y0;
  let maxX = x0;
  let maxY = y0;
  const rim = (q: number) => {
    if (mark[q] === 0) mark[q] = 2;
  };
  // scanline fill: walk left/right along each row, seed the rows above and below
  while (stack.length) {
    const p = stack.pop()!;
    if (mark[p] === 1) continue;
    if (!match(p * 4)) {
      rim(p);
      continue;
    }
    const y = Math.floor(p / w);
    const x = p - y * w;
    let xl = x;
    while (xl > 0 && mark[p - (x - xl) - 1] !== 1 && match((p - (x - xl) - 1) * 4)) xl--;
    let xr = x;
    while (xr < w - 1 && mark[p + (xr - x) + 1] !== 1 && match((p + (xr - x) + 1) * 4)) xr++;
    if (xl > 0) rim(y * w + xl - 1);
    if (xr < w - 1) rim(y * w + xr + 1);
    for (let cx = xl; cx <= xr; cx++) {
      const q = y * w + cx;
      mark[q] = 1;
      if (y > 0 && mark[q - w] !== 1) stack.push(q - w);
      if (y < h - 1 && mark[q + w] !== 1) stack.push(q + w);
    }
    if (xl < minX) minX = xl;
    if (xr > maxX) maxX = xr;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  // the rim extends the changed rect by a pixel
  minX = Math.max(0, minX - 1);
  minY = Math.max(0, minY - 1);
  maxX = Math.min(w - 1, maxX + 1);
  maxY = Math.min(h - 1, maxY + 1);
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const q = y * w + x;
      const m = mark[q];
      if (!m) continue;
      const i = q * 4;
      const da = d[i + 3]! / 255;
      if (m === 1) {
        // source-over: the fill colour at `a` over the pixel
        const oa = a + da * (1 - a);
        if (oa > 0) {
          d[i] = Math.round((r * a + d[i]! * da * (1 - a)) / oa);
          d[i + 1] = Math.round((g * a + d[i + 1]! * da * (1 - a)) / oa);
          d[i + 2] = Math.round((b * a + d[i + 2]! * da * (1 - a)) / oa);
        }
        d[i + 3] = Math.round(oa * 255);
      } else {
        // destination-over: the fill colour at `a` underneath the rim pixel
        const oa = da + a * (1 - da);
        if (oa > 0) {
          d[i] = Math.round((d[i]! * da + r * a * (1 - da)) / oa);
          d[i + 1] = Math.round((d[i + 1]! * da + g * a * (1 - da)) / oa);
          d[i + 2] = Math.round((d[i + 2]! * da + b * a * (1 - da)) / oa);
        }
        d[i + 3] = Math.round(oa * 255);
      }
    }
  }
  const rect = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  backup(rect);
  ctx.putImageData(img, 0, 0, rect.x, rect.y, rect.w, rect.h);
  return rect;
}

/** Colour of a pixel of the layer, or null when it is (nearly) transparent or out of bounds. */
export function pickColor(canvas: HTMLCanvasElement, x: number, y: number): string | null {
  const px = Math.floor(x);
  const py = Math.floor(y);
  if (px < 0 || py < 0 || px >= canvas.width || py >= canvas.height) return null;
  const d = canvas.getContext('2d')?.getImageData(px, py, 1, 1).data;
  if (!d || d[3]! < 8) return null;
  return toHex({ r: d[0]!, g: d[1]!, b: d[2]! });
}
